import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  CardContent,
} from "@mui/material";
import SideNav from "../../../agentComponents/sidenav";
import AgentAccordionFaq from "./AgentFaq";
import { useFormik } from "formik";
import * as Yup from "yup";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import { useLocation } from "react-router-dom";
import CustomSnackbar from "../../../components/CustomSnackbar";
import axiosInstance from "../../../services/axiosInstance";

const AgentDashboard = () => {
  const location = useLocation();
  const token = localStorage.getItem("token");
  const vendorNo = localStorage.getItem("vendorNo");

  const [userDetails, setUserDetails] = useState([]);
  const [faqDetails, setFaqDetails] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const [connectedCall, setConnectedCall] = useState({
    leadID: "",
    vendorID: "",
    listID: "",
    phoneNumber: "",
    callDate: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const leadId = params.get("lead_id");
    const vendorId = params.get("did_pattern");
    const listId = params.get("list_id");
    const phoneNumber = params.get("phone_number");
    const callDate = params.get("entry_date");

    setConnectedCall({
      leadID: leadId,
      vendorID: vendorId,
      listID: listId,
      phoneNumber,
      callDate,
    });
    localStorage.setItem("vendorNo", vendorId);
  }, [location]);

  useEffect(() => {
    if (connectedCall?.phoneNumber) {
      formik.setFieldValue("didNumber", vendorNo);
    }
  }, [connectedCall?.phoneNumber]);

  useEffect(() => {
    if (Object.values(connectedCall).some(Boolean)) {
      webHookData(connectedCall);
    }
  }, [connectedCall]);

  const webHookData = async (data) => {
    try {
      await axiosInstance.post(apiEndPointsConfig.saveWebHookData, data);
    } catch (error) {
      console.error(error);
    }
  };

  const getDetailsByDidNo = async (phoneNumber) => {
    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.searchByDID}/${phoneNumber}`
      );

      if (res.data.success) {
        setFaqDetails(res?.data?.faqs);
        setUserDetails(res?.data?.user);
        localStorage.setItem("didNumber", phoneNumber);
        localStorage.setItem("userId", res?.data?.user?._id);
        localStorage.setItem("businessName", res?.data?.user?.companyName);
        setSnackbar({
          open: true,
          message: "DID data fetched successfully!",
          severity: "success",
        });
      } else {
        setFaqDetails([]);
        setUserDetails([]);
        setSnackbar({
          open: true,
          message: "No user found for this DID number.",
          severity: "warning",
        });
      }
    } catch (error) {
      console.error(error);
      setFaqDetails([]);
      setUserDetails([]);
      setSnackbar({
        open: true,
        message: "No user found for this DID number.",
        severity: "warning",
      });
    }
  };

  const formik = useFormik({
    initialValues: { didNumber: "" },
    validationSchema: Yup.object({
      didNumber: Yup.string()
        .required("DID number is required")
        .matches(/^\d+$/, "Only numbers are allowed"),
    }),
    onSubmit: ({ didNumber }) => {
      const did = didNumber === "null" ? vendorNo : didNumber;
      getDetailsByDidNo(did);
    },
  });

  return (
    <Box sx={{ display: "flex", p: { xs: "50px 0 0 0", md: "50px 20px" } }}>
      <SideNav />
      <Box sx={{ width: "100%" }}>
        <Box sx={{ padding: "24px" }}>
          <Typography variant="h4" fontWeight={900} color="#424242" mt={2}>
            {userDetails.companyName == undefined ? (
              "Agent Dashboard"
            ) : (
              <span
                style={{
                  color: "#0C237B",
                }}
              >
                {userDetails.companyName}
              </span>
            )}
          </Typography>

          <Grid container sx={{ mt: "20px" }}>
            <Grid item md={4} xs={12}>
              <form onSubmit={formik.handleSubmit}>
                <Box sx={{ mt: "20px", maxWidth: "400px" }}>
                  <Typography variant="body1" color="textSecondary">
                    Enter DID Number <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    id="didNumber"
                    name="didNumber"
                    placeholder="Enter DID Number"
                    variant="outlined"
                    fullWidth
                    value={formik.values.didNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.didNumber &&
                      Boolean(formik.errors.didNumber)
                    }
                    helperText={
                      formik.touched.didNumber && formik.errors.didNumber
                    }
                    sx={{
                      mt: 1,
                      mb: 2,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: "#1e1b4b",
                      "&:hover": { backgroundColor: "#2e2a5b" },
                    }}
                  >
                    Search
                  </Button>
                </Box>
              </form>
            </Grid>

            {userDetails.length === 0 ? null : (
              <Grid item md={8} xs={12}>
                <Grid container spacing={2}>
                  {[
                    {
                      label: "Full Name",
                      value: userDetails?.name,
                    },
                    {
                      label: "Email",
                      value: userDetails?.email,
                    },
                    {
                      label: "Phone Number",
                      value: userDetails?.phoneNumber,
                    },
                    {
                      label: "Company Name",
                      value: userDetails?.companyName,
                    },
                    {
                      label: "Industry Type",
                      value: userDetails?.industryType,
                    },
                    {
                      label: "Your Call Center Number",
                      value: userDetails?.customerCareNo,
                    },
                  ].map((item, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px solid #f0f0f0",
                          borderRadius: 1,
                        }}
                      >
                        <Typography color="grey">{item.label}</Typography>
                        <Typography fontWeight={900} color="grey">
                          {item.value || "-"}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 6 }}>
            <AgentAccordionFaq faqDetails={faqDetails} />
          </Box>

          <CustomSnackbar
            open={snackbar.open}
            onClose={handleCloseSnackbar}
            message={snackbar.message}
            severity={snackbar.severity}
            vertical="bottom"
            horizontal="right"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AgentDashboard;
